/*
  Beauvyn scalable premium blog template
  File: assets/bv-article.js
*/

(() => {
  function normaliseText(value) {
    return value
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function slugify(value) {
    return value
      .toLowerCase()
      .trim()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function createTocLink(heading) {
    const listItem = document.createElement("li");
    const link = document.createElement("a");

    link.href = `#${heading.id}`;
    link.textContent =
      heading.dataset.tocLabel || heading.textContent.trim();

    listItem.appendChild(link);

    return {
      listItem,
      link
    };
  }

  /*
    Wraps article tables so they scroll safely on mobile.
  */

  function wrapTables(content) {
    content.querySelectorAll("table").forEach((table) => {
      const existingWrapper = table.closest(".bv-table-wrap");

      if (existingWrapper) {
        return;
      }

      const wrapper = document.createElement("div");
      wrapper.className = "bv-table-wrap";

      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }

  /*
    Adds the highlighted quick-check design to selected paragraphs.
  */

  function addQuickCheckStyles(content) {
    content.querySelectorAll("p").forEach((paragraph) => {
      const paragraphText = paragraph.textContent
        .trim()
        .toLowerCase();

      if (
        paragraphText.startsWith(
          "look for a clear statement such as"
        ) ||
        paragraphText.startsWith("pouch price ÷") ||
        paragraphText.startsWith("pouch price /") ||
        paragraphText.startsWith("quick check:")
      ) {
        paragraph.classList.add("bv-quick-check");
      }
    });
  }

  /*
    Prepares all H2 headings inside the article.

    It:
    - Finds every H2.
    - Hides the first H2 only when it repeats the article title.
    - Converts headings beginning with 1., 2., 3. into numbered designs.
    - Adds unique IDs for the table of contents.
  */

  function prepareHeadings(content, articleTitle) {
    let headings = Array.from(
      content.querySelectorAll("h2")
    );

    if (headings.length > 0) {
      const firstHeadingText = normaliseText(
        headings[0].textContent
      );

      const articleTitleText = normaliseText(articleTitle);

      if (
        firstHeadingText &&
        articleTitleText &&
        (
          firstHeadingText === articleTitleText ||
          firstHeadingText.startsWith(`${articleTitleText} `)
        )
      ) {
        headings[0].hidden = true;
        headings = headings.slice(1);
      }
    }

    headings.forEach((heading, index) => {
      const originalText = heading.textContent.trim();

      heading.dataset.tocLabel = originalText;

      /*
        Recognises headings written as:

        1. Check the collagen amount
        2) Look for hydrolysed collagen peptides
      */

      const numberedHeading = originalText.match(
        /^(\d+)[.)]\s+(.+)$/
      );

      if (numberedHeading) {
        const sectionNumber = numberedHeading[1];
        const headingText = numberedHeading[2];

        heading.dataset.sectionNumber = sectionNumber;
        heading.classList.add("bv-numbered-heading");
        heading.innerHTML = "";

        const numberElement = document.createElement("span");

        numberElement.className = "bv-heading-number";
        numberElement.setAttribute("aria-hidden", "true");
        numberElement.textContent = String(sectionNumber).padStart(
          2,
          "0"
        );

        const textElement = document.createElement("span");

        textElement.className = "bv-heading-text";
        textElement.textContent = headingText;

        heading.append(numberElement, textElement);
      }

      if (!heading.id) {
        const baseId =
          slugify(originalText) ||
          `article-section-${index + 1}`;

        let uniqueId = baseId;
        let suffix = 2;

        while (document.getElementById(uniqueId)) {
          uniqueId = `${baseId}-${suffix}`;
          suffix += 1;
        }

        heading.id = uniqueId;
      }
    });

    return headings;
  }

  /*
    Builds the desktop and mobile table of contents
    using the H2 headings inside the blog content.
  */

  function buildTableOfContents(article, headings) {
    const desktopToc = article.querySelector(
      "[data-bv-desktop-toc]"
    );

    const mobileToc = article.querySelector(
      "[data-bv-mobile-toc]"
    );

    const desktopLinks = new Map();

    if (desktopToc) {
      desktopToc.innerHTML = "";
    }

    if (mobileToc) {
      mobileToc.innerHTML = "";
    }

    headings.forEach((heading) => {
      if (heading.hidden) {
        return;
      }

      if (desktopToc) {
        const desktopItem = createTocLink(heading);

        desktopToc.appendChild(desktopItem.listItem);
        desktopLinks.set(heading.id, desktopItem.link);
      }

      if (mobileToc) {
        const mobileItem = createTocLink(heading);

        mobileToc.appendChild(mobileItem.listItem);
      }
    });

    return desktopLinks;
  }

  /*
    Highlights the current article section in the
    desktop table of contents.
  */

  function observeCurrentHeading(headings, desktopLinks) {
    if (
      !("IntersectionObserver" in window) ||
      desktopLinks.size === 0
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (firstEntry, secondEntry) =>
              firstEntry.boundingClientRect.top -
              secondEntry.boundingClientRect.top
          );

        if (visibleEntries.length === 0) {
          return;
        }

        desktopLinks.forEach((link) => {
          link.classList.remove("is-active");
        });

        const activeHeading = visibleEntries[0].target;
        const activeLink = desktopLinks.get(activeHeading.id);

        if (activeLink) {
          activeLink.classList.add("is-active");
        }
      },
      {
        rootMargin: "-18% 0px -68% 0px",
        threshold: 0
      }
    );

    headings.forEach((heading) => {
      observer.observe(heading);
    });
  }

  /*
    Places the product card at the exact location of:

    [[BEAUVYN_PRODUCT]]

    The marker must be on its own line in the Shopify blog editor.
  */

 function placeInlineProduct() {
  const articleContent = document.querySelector(
    '[data-bv-article-content]'
  );

  const productCard = document.querySelector(
    '[data-bv-inline-product]'
  );

  if (!articleContent || !productCard) {
    return;
  }

  const marker = articleContent.querySelector(
    '[data-bv-product-marker]'
  );

  if (!marker) {
    productCard.hidden = true;
    return;
  }

  /*
   * Shopify will normally place the marker inside a paragraph.
   * Replacing the paragraph prevents an empty gap being left behind.
   */
  const markerWrapper = marker.closest('p');

  if (markerWrapper) {
    markerWrapper.replaceWith(productCard);
  } else {
    marker.replaceWith(productCard);
  }

  productCard.hidden = false;
  productCard.removeAttribute('aria-hidden');
}

  function initialiseArticle(article) {
    if (article.dataset.bvInitialised === "true") {
      return;
    }

    article.dataset.bvInitialised = "true";

    const content = article.querySelector(
      "[data-bv-article-content]"
    );

    if (!content) {
      return;
    }

    const articleTitle =
      article.dataset.articleTitle || "";

    const headings = prepareHeadings(
      content,
      articleTitle
    );

    wrapTables(content);
    addQuickCheckStyles(content);

    const desktopLinks = buildTableOfContents(
      article,
      headings
    );

    /*
      Insert the product card at the marker location.
    */

    placeInlineProduct(
      article,
      content
    );

    observeCurrentHeading(
      headings,
      desktopLinks
    );
  }

  function initialiseAllArticles() {
    document
      .querySelectorAll("[data-beauvyn-article]")
      .forEach(initialiseArticle);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initialiseAllArticles
    );
  } else {
    initialiseAllArticles();
  }

  document.addEventListener(
    "shopify:section:load",
    initialiseAllArticles
  );
})();